import { useCallback, useEffect, useRef, useState } from "react";
import { ChatCircle, PaperPlaneRight } from "@phosphor-icons/react";
import { listAllianceMessages, postAllianceMessage, formatApiError } from "@/lib/api";
import PremiumBadge from "@/components/PremiumBadge";
import RankIcon from "@/components/RankIcon";
import { toast } from "sonner";

const POLL_MS = 4000;
const MAX_LEN = 240;

function fmtTime(iso) {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * AllianceChat — lightweight polling-based group chat strip.
 *
 * Polls /api/alliances/{aid}/messages every POLL_MS (only when the alliance is
 * present and the panel is mounted). Posts go through the standard auth-bearer
 * axios instance.
 */
export default function AllianceChat({ allianceId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastIdsRef = useRef(new Set()); // de-dup helper across polls

  // ---- Polling ----
  useEffect(() => {
    if (!allianceId) return;
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const r = await listAllianceMessages(allianceId);
        if (!alive) return;
        const fresh = r.messages || [];
        const ids = new Set(fresh.map((m) => m.id));
        const prevSize = lastIdsRef.current.size;
        lastIdsRef.current = ids;
        setMessages(fresh);
        // Auto-scroll only when new messages arrived (not on every poll)
        if (ids.size !== prevSize && listRef.current) {
          requestAnimationFrame(() => {
            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
          });
        }
      } catch (_e) {
        /* swallow — chat is best-effort */
      }
      timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [allianceId]);

  // ---- Send ----
  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const msg = await postAllianceMessage(allianceId, text.slice(0, MAX_LEN));
      // Optimistic append — server response is the canonical record
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      lastIdsRef.current.add(msg.id);
      setDraft("");
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  }, [draft, sending, allianceId]);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div className="flex flex-col h-full border-t-2 border-white/10" data-testid="alliance-chat">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 shrink-0 bg-white/[0.02]">
        <ChatCircle size={12} className="text-cyan-300" />
        <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
          Group Chat
        </span>
        <span className="ml-auto mono text-[9px] text-zinc-600">{messages.length}</span>
      </div>
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto px-2 py-1.5 space-y-1.5"
        data-testid="alliance-chat-list"
      >
        {messages.length === 0 ? (
          <div className="p-3 text-center mono text-[10px] text-zinc-600 uppercase tracking-widest">
            — say hi to your group —
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.user_id === currentUserId;
            return (
              <div
                key={m.id}
                data-testid={`chat-msg-${m.id}`}
                className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
              >
                <div className="flex items-baseline gap-1.5">
                  {m.is_premium && <PremiumBadge size={10} />}
                  <RankIcon rank={m.rank} size={10} />
                  <span className={`mono text-[9px] uppercase tracking-widest ${
                    mine ? "text-cyan-300" : "text-orange-300"
                  }`}>
                    {mine ? "You" : m.user_name}
                  </span>
                  <span className="mono text-[9px] text-zinc-600">{fmtTime(m.created_at)}</span>
                </div>
                <div
                  className={`text-[11px] leading-snug px-2 py-1 max-w-[90%] break-words ${
                    mine
                      ? "bg-cyan-400/10 text-cyan-100 border border-cyan-400/20"
                      : "bg-white/5 text-zinc-200 border border-white/5"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-center gap-1 px-2 py-2 border-t border-white/5 shrink-0 bg-[#0B0E12]">
        <input
          data-testid="chat-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={handleKey}
          placeholder="Message your group…"
          maxLength={MAX_LEN}
          className="flex-1 bg-black/40 border border-white/10 focus:border-cyan-400/50 outline-none mono text-[11px] text-white px-2 py-1.5"
        />
        <button
          type="button"
          data-testid="chat-send-btn"
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          title="Send"
          className="border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-40 disabled:hover:bg-transparent px-2 py-1.5"
        >
          <PaperPlaneRight size={13} weight="fill" />
        </button>
      </div>
    </div>
  );
}
